import api from './api';
import type {
  OrgUser,
  OrgUsersResponse,
  Invitation,
  UserSession,
  AssignableWorkspaceRole,
} from '../types';

/** What removing a member costs this workspace — for the remove confirm (P1). */
export interface UserDeletionImpact {
  isOwner: boolean;
  queries30d: number;
  lastLoginAt: string | null;
  sourcesWithAccess: Array<{ id: string; name: string }>;
  soleGranteeSources: Array<{ id: string; name: string }>;
}

export const teamApi = {
  // Users
  listUsers: async (): Promise<OrgUsersResponse> => {
    const { data } = await api.get('/api/org/users');
    return data;
  },

  /** Impact preview shown before removing a member (queries, sources, orphan risk). Admin-only. */
  getDeletionImpact: async (userId: string): Promise<UserDeletionImpact> => {
    const { data } = await api.get(`/api/org/users/${userId}/deletion-impact`);
    return data.impact as UserDeletionImpact;
  },

  changeRole: async (userId: string, role: AssignableWorkspaceRole): Promise<OrgUser> => {
    const { data } = await api.patch(`/api/org/users/${userId}/role`, { role });
    return data;
  },

  suspendUser: async (userId: string): Promise<OrgUser> => {
    const { data } = await api.post(`/api/org/users/${userId}/suspend`);
    return data;
  },

  reactivateUser: async (userId: string): Promise<OrgUser> => {
    const { data } = await api.post(`/api/org/users/${userId}/reactivate`);
    return data;
  },

  removeUser: async (userId: string): Promise<void> => {
    await api.delete(`/api/org/users/${userId}`);
  },

  forceSignOut: async (userId: string): Promise<void> => {
    await api.post(`/api/org/users/${userId}/revoke-sessions`);
  },

  // Invitations
  createInvitation: async (email: string, role: AssignableWorkspaceRole): Promise<Invitation> => {
    const { data } = await api.post('/api/org/users/invitations', { email, role });
    return data;
  },

  listInvitations: async (status?: string): Promise<{ invitations: Invitation[] }> => {
    const params = status ? `?status=${status}` : '';
    const { data } = await api.get(`/api/org/users/invitations${params}`);
    return data;
  },

  revokeInvitation: async (id: string): Promise<void> => {
    await api.delete(`/api/org/users/invitations/${id}`);
  },

  // Ownership
  transferOwnership: async (targetUserId: string, password: string): Promise<void> => {
    await api.post('/api/org/ownership/transfer', { targetUserId, password });
  },

  // Sessions (current user)
  listSessions: async (): Promise<{ sessions: UserSession[] }> => {
    const { data } = await api.get('/api/auth/sessions');
    return data;
  },

  revokeSession: async (sessionId: string): Promise<void> => {
    await api.delete(`/api/auth/sessions/${sessionId}`);
  },

  revokeAllOtherSessions: async (): Promise<void> => {
    await api.delete('/api/auth/sessions');
  },
};
